import { AppPaths, Storage } from "@/shared/common/enums";
import { Header, SidebarMenu } from "@/shared/components";
import { useAppDispatch, useAppSelector, useMediaQuery, useTrackPageView } from "@/shared/hooks";
import { useLazyGetStopToolUrlQuery } from "@/shared/services";
import {
  setAccountActiveTab,
  setActiveAccountId,
  setActiveApplicationId,
  setActiveBeneficiaryId,
  setActiveCardId,
  setActiveDepositId,
  setActiveLoanId,
  setActiveTransactionId,
  setBeneficiaryActiveTab,
  setLoanActiveTab,
} from "@/shared/state/common";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { useSideNavigationControls } from "@ab.uitools/ui-library/hooks/useSideNavigation";
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

export const Layout = () => {
  useTrackPageView();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const activeItem = useAppSelector(state => state.common);
  const isAuth = localStorage.getItem(Storage.IsAuth);
  const [ref, setRef] = useState<HTMLElement | null>(null);

  const [getUrl, { isFetching: isStopToolLoading }] = useLazyGetStopToolUrlQuery();

  const {
    onOpen,
    isOpen,
    onClose,
    isPined,
  } = useSideNavigationControls();
  const isDesktop = useMediaQuery();

  const resetActiveAccountItem = () => {
    if (
      !location.pathname.includes(AppPaths.Accounts) &&
      !!activeItem.accountId
    ) {
      dispatch(setAccountActiveTab(null));
      dispatch(setActiveAccountId(null));
    }
  };

  const resetActiveCardItem = () => {
    if (
      !location.pathname.includes(AppPaths.Cards) &&
      !!activeItem.cardId
    ) {
      dispatch(setActiveCardId(null));
    }
  };

  const resetActiveLoanItem = () => {
    if (
      !location.pathname.includes(AppPaths.Loans) &&
      !!activeItem.loanId
    ) {
      dispatch(setLoanActiveTab(null));
      dispatch(setActiveLoanId(null));
    }
  };

  const resetActiveApplicationItem = () => {
    if (
      !location.pathname.includes(AppPaths.Applications) &&
      !!activeItem.applicationId
    ) {
      dispatch(setActiveApplicationId(null));
    }
  };

  const resetActiveTransactionItem = () => {
    if (
      !location.pathname.includes(AppPaths.Transaction) &&
      !!activeItem.transactionId
    ) {
      dispatch(setActiveTransactionId(null));
    }
  };

  const resetActiveDepositItem = () => {
    if (
      !location.pathname.includes(AppPaths.Deposits) &&
      !!activeItem.depositId
    ) {
      dispatch(setActiveDepositId(null));
    }
  };

  const resetActiveBeneficiaryItem = () => {
    if (
      !location.pathname.includes(AppPaths.Beneficiaries) &&
      !!activeItem.beneficiaryId
    ) {
      dispatch(setActiveBeneficiaryId(null));
      dispatch(setBeneficiaryActiveTab(null));
    }
  };

  const getStopToolUrl = () => {
    getUrl().unwrap()
      .then((url) => {
        window.open(url, "_blank");
      });
  };

  useEffect(() => {
    if (isOpen && !isDesktop) {
      document.body.style.overflow = "clip";
    } else {
      document.body.style.overflow = "initial";
    }
  }, [isOpen]);

  useEffect(() => {
    resetActiveAccountItem();
    resetActiveCardItem();
    resetActiveLoanItem();
    resetActiveTransactionItem();
    resetActiveApplicationItem();
    resetActiveDepositItem();
    resetActiveBeneficiaryItem();
  }, [location.pathname]);

  if (!isAuth) {
    return <Navigate to={AppPaths.Login} replace/>;
  }

  return (
    <>
      <div className="layout flexbox full-width">
        <SidebarMenu
          onOpen={onOpen}
          isOpen={isDesktop ? true : isOpen}
          onClose={onClose}
          additionalRef={ref as HTMLElement}
          isPined={isDesktop ? true : isPined}
        />
        <div className="layout--container">
          <Header
            onOpen={onOpen}
            isOpen={isOpen}
            setRef={setRef}
            getStopToolUrl={getStopToolUrl}
          />
          {isOpen && !isDesktop && (
            <div className="mobile-overlay"/>
          )}
          <div className="children-wrapper">
            <div className="rounded-wrapper">
              <div className="rounded-element"/>
            </div>
            <Outlet/>
          </div>
        </div>
      </div>
      {isStopToolLoading && (
        <div className="layout-loader">
          <Spinner size="large"/>
        </div>
      )}
    </>
  );
};
